import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function slugify(input: string) {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80) || "plats";
}

const PUBLIC_SPOT_FIELDS = "id, slug, title, description, region, spot_type, latitude, longitude, image_urls, created_at, updated_at, user_id, author_name, editorial, photo_credit, price_info, season_info, booking_info, stay_length_info, has_electricity, has_fresh_water, has_grey_water, has_latrine, has_toilet, has_shower, has_waste_bins, surface, level_info, max_vehicle_length_m, access_info, noise_info, mobile_coverage, pets_info, children_info, accessibility_info, nearest_services, best_for, not_suitable_for, backup_plan, editorial_verdict, visited_at, verified_at, fact_status, source_urls";

export const listSpots = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ region: z.string().optional() }).parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    let q = supabase
      .from("camping_spots")
      .select(PUBLIC_SPOT_FIELDS)
      .eq("published", true)
      .order("editorial", { ascending: true })
      .order("created_at", { ascending: false })
      .limit(200);
    if (data.region) q = q.eq("region", data.region);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { spots: rows ?? [] };
  });

export const getSpotBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(120) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: spot, error } = await supabase
      .from("camping_spots")
      .select(PUBLIC_SPOT_FIELDS)
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!spot) return { spot: null, author: null, comments: [] };

    const [{ data: author }, { data: comments }] = await Promise.all([
      spot.user_id
        ? supabase.from("public_profiles").select("id, display_name, avatar_url").eq("id", spot.user_id).maybeSingle()
        : Promise.resolve({ data: null as { id: string; display_name: string | null; avatar_url: string | null } | null }),
      supabase
        .from("spot_comments")
        .select("id, body, created_at, user_id")
        .eq("spot_id", spot.id)
        .order("created_at", { ascending: true })
        .limit(200),
    ]);

    const userIds = Array.from(new Set((comments ?? []).map((c) => c.user_id)));
    let commenters: Record<string, { display_name: string | null; avatar_url: string | null }> = {};
    if (userIds.length) {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("id, display_name, avatar_url")
        .in("id", userIds);
      for (const p of profs ?? []) commenters[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url };
    }
    const enriched = (comments ?? []).map((c) => ({
      ...c,
      author: commenters[c.user_id] ?? { display_name: null, avatar_url: null },
    }));

    return { spot, author: author ?? null, comments: enriched };
  });

const CreateSpotSchema = z.object({
  title: z.string().trim().min(3).max(120),
  description: z.string().trim().min(20).max(6000),
  region: z.string().trim().max(80).optional().or(z.literal("")),
  spot_type: z.string().trim().max(60).optional().or(z.literal("")),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  image_urls: z.array(z.string().url().max(600)).max(10).optional(),
  price_info: z.string().trim().max(200).optional().or(z.literal("")),
  season_info: z.string().trim().max(200).optional().or(z.literal("")),
  booking_info: z.string().trim().max(300).optional().or(z.literal("")),
  stay_length_info: z.string().trim().max(120).optional().or(z.literal("")),
  has_electricity: z.boolean().nullable().optional(),
  has_fresh_water: z.boolean().nullable().optional(),
  has_grey_water: z.boolean().nullable().optional(),
  has_latrine: z.boolean().nullable().optional(),
  has_toilet: z.boolean().nullable().optional(),
  has_shower: z.boolean().nullable().optional(),
  has_waste_bins: z.boolean().nullable().optional(),
  surface: z.string().trim().max(100).optional().or(z.literal("")),
  level_info: z.string().trim().max(200).optional().or(z.literal("")),
  max_vehicle_length_m: z.number().min(2).max(30).nullable().optional(),
  access_info: z.string().trim().max(600).optional().or(z.literal("")),
  noise_info: z.string().trim().max(300).optional().or(z.literal("")),
  mobile_coverage: z.string().trim().max(200).optional().or(z.literal("")),
  pets_info: z.string().trim().max(200).optional().or(z.literal("")),
  children_info: z.string().trim().max(200).optional().or(z.literal("")),
  accessibility_info: z.string().trim().max(300).optional().or(z.literal("")),
  nearest_services: z.string().trim().max(400).optional().or(z.literal("")),
  best_for: z.string().trim().max(300).optional().or(z.literal("")),
  not_suitable_for: z.string().trim().max(300).optional().or(z.literal("")),
  backup_plan: z.string().trim().max(300).optional().or(z.literal("")),
  visited_at: z.string().date().nullable().optional(),
  fact_status: z.enum(["verified", "experience", "check_before_trip"]).nullable().optional(),
  source_urls: z.array(z.string().url().max(600)).max(5).optional(),
});

export const createSpot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateSpotSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const baseSlug = slugify(data.title);
    const slug = `${baseSlug}-${Math.random().toString(36).slice(2, 7)}`;
    const { data: row, error } = await supabase
      .from("camping_spots")
      .insert({
        user_id: userId,
        title: data.title,
        description: data.description,
        region: data.region || null,
        spot_type: data.spot_type || null,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        image_urls: data.image_urls ?? [],
         price_info: data.price_info || null,
         season_info: data.season_info || null,
         booking_info: data.booking_info || null,
         stay_length_info: data.stay_length_info || null,
         has_electricity: data.has_electricity ?? null,
         has_fresh_water: data.has_fresh_water ?? null,
         has_grey_water: data.has_grey_water ?? null,
         has_latrine: data.has_latrine ?? null,
         has_toilet: data.has_toilet ?? null,
         has_shower: data.has_shower ?? null,
         has_waste_bins: data.has_waste_bins ?? null,
         surface: data.surface || null,
         level_info: data.level_info || null,
         max_vehicle_length_m: data.max_vehicle_length_m ?? null,
         access_info: data.access_info || null,
         noise_info: data.noise_info || null,
         mobile_coverage: data.mobile_coverage || null,
         pets_info: data.pets_info || null,
         children_info: data.children_info || null,
         accessibility_info: data.accessibility_info || null,
         nearest_services: data.nearest_services || null,
         best_for: data.best_for || null,
         not_suitable_for: data.not_suitable_for || null,
         backup_plan: data.backup_plan || null,
         visited_at: data.visited_at ?? null,
         fact_status: data.fact_status ?? "experience",
         source_urls: data.source_urls ?? [],
        slug,
      })
      .select("slug")
      .single();
    if (error) throw new Error(error.message);
    return { slug: row.slug };
  });

export const listMySpots = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("camping_spots")
      .select("id, slug, title, region, spot_type, image_urls, created_at, published")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { spots: data ?? [] };
  });

export const deleteMySpot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("camping_spots").delete().eq("id", data.id).eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const addSpotComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ spot_id: z.string().uuid(), body: z.string().trim().min(2).max(2000) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("spot_comments")
      .insert({ spot_id: data.spot_id, user_id: userId, body: data.body });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
